import { isVariance, type Variance, VARIANCES } from './variance';

export const SDLY_COLUMNS = {
  SDLY: 'sdly',
  NET_SDLY: 'net_sdly',
} as const;

export type SdlyColumn = (typeof SDLY_COLUMNS)[keyof typeof SDLY_COLUMNS];

export function isSdlyColumn(value: unknown): value is SdlyColumn {
  return Object.values(SDLY_COLUMNS).includes(value as SdlyColumn);
}

export const FORECAST_COLUMNS = {
  BI: 'bi_forecast',
  USER: 'user_forecast',
  RMS: 'rms_forecast',
  FINANCE: 'finance_forecast',
} as const;

export type ForecastColumn =
  (typeof FORECAST_COLUMNS)[keyof typeof FORECAST_COLUMNS];

export function isForecastColumn(value: unknown): value is ForecastColumn {
  return Object.values(FORECAST_COLUMNS).includes(value as ForecastColumn);
}

export const GROUP_COLUMNS_BY = {
  CATEGORIES: 'categories',
  KPIS: 'kpis',
} as const;

export type GroupColumnsBy =
  (typeof GROUP_COLUMNS_BY)[keyof typeof GROUP_COLUMNS_BY];

export function isGroupColumnsBy(value: unknown): value is GroupColumnsBy {
  return Object.values(GROUP_COLUMNS_BY).includes(value as GroupColumnsBy);
}

export const SORT_ON = {
  ACTUALS: 'actuals',
  VARIANCE: 'variance',
} as const;

export type SortOn = (typeof SORT_ON)[keyof typeof SORT_ON];

export function isSortOn(value: unknown): value is SortOn {
  return Object.values(SORT_ON).includes(value as SortOn);
}

export interface DataPreferences {
  forecastColumn: ForecastColumn;
  sdlyColumn: SdlyColumn;
  variance: Variance;
}

export const DEFAULT_DATA_PREFERENCES: Readonly<DataPreferences> =
  Object.freeze({
    forecastColumn: FORECAST_COLUMNS.BI,
    sdlyColumn: SDLY_COLUMNS.SDLY,
    variance: VARIANCES.NONE,
  });

export function parseDataPreferences(json: unknown): DataPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_DATA_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    forecastColumn: isForecastColumn(raw.forecastColumn)
      ? raw.forecastColumn
      : DEFAULT_DATA_PREFERENCES.forecastColumn,
    sdlyColumn: isSdlyColumn(raw.sdlyColumn)
      ? raw.sdlyColumn
      : DEFAULT_DATA_PREFERENCES.sdlyColumn,
    variance: isVariance(raw.variance)
      ? raw.variance
      : DEFAULT_DATA_PREFERENCES.variance,
  };
}

export interface TableLayoutPreferences {
  groupColumnsBy: GroupColumnsBy;
  sortOn: SortOn;
}

export const DEFAULT_TABLE_LAYOUT_PREFERENCES: Readonly<TableLayoutPreferences> =
  Object.freeze({
    groupColumnsBy: GROUP_COLUMNS_BY.CATEGORIES,
    sortOn: SORT_ON.ACTUALS,
  });

export function parseTableLayoutPreferences(
  json: unknown,
): TableLayoutPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_TABLE_LAYOUT_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    groupColumnsBy: isGroupColumnsBy(raw.groupColumnsBy)
      ? raw.groupColumnsBy
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.groupColumnsBy,
    sortOn: isSortOn(raw.sortOn)
      ? raw.sortOn
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.sortOn,
  };
}
